import type { ReservationBookingClosure } from '../models/data';

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const MAX_BOOKING_CLOSURES = 100;

const toMinutes = (value: string): number | null => {
  const match = TIME_PATTERN.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

export const normalizeBookingClosures = (value: unknown): ReservationBookingClosure[] => {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const normalized: ReservationBookingClosure[] = [];
  value.slice(0, MAX_BOOKING_CLOSURES).forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const candidate = item as Record<string, unknown>;
    const date = typeof candidate.date === 'string' ? candidate.date.trim() : '';
    const from = typeof candidate.from === 'string' ? candidate.from.trim() : '';
    const to = typeof candidate.to === 'string' ? candidate.to.trim() : '';
    const fromMinutes = toMinutes(from);
    const toMinutesValue = toMinutes(to);
    if (
      !DATE_KEY_PATTERN.test(date)
      || fromMinutes === null
      || toMinutesValue === null
      || fromMinutes >= toMinutesValue
    ) return;

    const key = `${date}:${from}:${to}`;
    if (seen.has(key)) return;
    seen.add(key);
    const rawId = typeof candidate.id === 'string' ? candidate.id.trim() : '';
    normalized.push({
      id: rawId || `booking-closure-${key}-${index}`,
      date,
      from,
      to,
      ...(typeof candidate.reason === 'string' && candidate.reason.trim()
        ? { reason: candidate.reason.trim().slice(0, 120) }
        : {}),
    });
  });

  return normalized.sort((a, b) =>
    a.date.localeCompare(b.date) || a.from.localeCompare(b.from) || a.to.localeCompare(b.to)
  );
};

export const addMinutesToTime = (value: string, minutesToAdd: number): string | null => {
  const startMinutes = toMinutes(value);
  if (startMinutes === null || !Number.isFinite(minutesToAdd) || minutesToAdd < 0) return null;
  const result = startMinutes + minutesToAdd;
  if (result >= 24 * 60) return null;
  return `${String(Math.floor(result / 60)).padStart(2, '0')}:${String(result % 60).padStart(2, '0')}`;
};

export const isReservationTimeClosed = (input: {
  dateKey: string;
  startTime: string;
  endTime: string;
  bookingClosures?: ReservationBookingClosure[] | null;
}): boolean => {
  const startMinutes = toMinutes(input.startTime);
  const endMinutes = toMinutes(input.endTime);
  if (
    !DATE_KEY_PATTERN.test(input.dateKey)
    || startMinutes === null
    || endMinutes === null
    || startMinutes >= endMinutes
  ) return false;

  return normalizeBookingClosures(input.bookingClosures).some(closure => {
    if (closure.date !== input.dateKey) return false;
    const closureStart = toMinutes(closure.from);
    const closureEnd = toMinutes(closure.to);
    return closureStart !== null
      && closureEnd !== null
      && startMinutes < closureEnd
      && endMinutes > closureStart;
  });
};
